import { PartialNullable } from "../../Extensions";
import { PathHelper } from "../../Helpers/PathHelper";
import { StringHelper } from "../../Helpers/StringHelper";
import { $$, IJNativeEvent } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyInt32 } from "../../RadObject";
import { ApplicationInformationService, WindowShim } from "../../RadWindow";
import { ClientAwareCategoryAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareCategoryAttribute";
import { ClientAwareDisplayNameAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDisplayNameAttribute";
import { ServiceManager } from "../../ServiceManager";
import { Control, ControlPropertyValues, IControl } from "./Control";
import { IPageDimmer, PageDimmer } from "./PageDimmer";
import { ProgressBar } from "./ProgressBar";
import { NAMESPACE } from "./_Namespace";

import "./FullPageLoader.scss";

const
    radWindow = WindowShim();

export enum FullPageLoaderModeEnum
{
    loader = "loader",
    progress = "progress",
}

export type FullPageLoaderPropertyValues =
    ControlPropertyValues
    & {
        mode: FullPageLoaderModeEnum;
        queueLength: number;
        total: number;
        value: number;
    };

export type FullPageLoaderProperties = {
    [name in keyof FullPageLoaderPropertyValues]: Property<FullPageLoaderPropertyValues[name]>
};

export interface IFullPageLoader extends IControl, FullPageLoaderProperties
{
    dequeue(): void;
    enqueue(): void;
}

export class FullPageLoader extends Control implements IFullPageLoader
{
    // #region Static

    public static override classNamespace = NAMESPACE + "FullPageLoader";
    public static override className = "FullPageLoader";

    // #region Properties

    public static instance?: FullPageLoader;

    // #endregion

    // #region Constructor

    static {
        $$(() =>
        {
            $$(document).on("ajaxSend", (evt: IJNativeEvent, eventData: any) =>
            {
                const
                    options = eventData.options;

                if (options.showLoading === undefined)
                    options.showLoading = true;

                if (options.showLoading && !(ServiceManager.getOptional(ApplicationInformationService)?.hideFullPageLoaderForSameOriginRequests && this.isRequestSameOrigin(options.url)))
                    radWindow.fullPageLoader?.enqueue();
            });

            $$(document).on("ajaxComplete", (evt: IJNativeEvent, eventData: any) =>
            {
                const
                    options = eventData.options;

                if (options.showLoading === undefined)
                    options.showLoading = true;

                if (options.showLoading && !(ServiceManager.getOptional(ApplicationInformationService)?.hideFullPageLoaderForSameOriginRequests && this.isRequestSameOrigin(options.url)))
                    radWindow.fullPageLoader?.dequeue();
            });
        });
    }

    // #endregion

    // #region Private Methods

    private static isRequestSameOrigin(url: string)
    {
        return PathHelper.getLocation(window.location).origin === PathHelper.getLocation(url).origin;
    }

    // #endregion

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<FullPageLoaderPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                mode: FullPageLoaderModeEnum.loader,
                queueLength: 0,
                text: "Please wait...",
                value: 0,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected dimmer: IPageDimmer;
    protected dimmerShown: boolean;
    protected progress?: ProgressBar;
    protected showDimmerTimeoutId: number;
    protected updatingDisplay: boolean;
    protected waitTimeInMilliseconds: number;

    // #endregion

    // #region Properties

    public mode!: Property<FullPageLoaderModeEnum>;
    public queueLength!: PropertyInt32;
    public total!: PropertyInt32;
    public value!: PropertyInt32;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<FullPageLoaderPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-full-page-loader content hidden ui active dimmer");

        this.dimmer = new PageDimmer({ cssClass: "rad-control-full-page-dimmer" });
        this.dimmerShown = false;
        this.showDimmerTimeoutId = 0;
        this.updatingDisplay = false;
        this.waitTimeInMilliseconds = 250;
    }

    // #endregion

    // #region Public Methods

    public dequeue()
    {
        this.queueLength.set(Math.max(0, (this.queueLength.get() ?? 0) - 1));
    }
    public enqueue()
    {
        this.queueLength.set((this.queueLength.get() ?? 0) + 1);
    }

    // #endregion

    // #region Protected Methods

    protected _modeChanged(change: PropertyChange<FullPageLoaderModeEnum>)
    {
        /* Intentionally empty */
    }
    protected _queueLengthChanged(change: PropertyChange<number>)
    {
        if (!change.originalValue && change.currentValue)
        {
            if (this.mode.get() === FullPageLoaderModeEnum.progress)
                this.progress?.reset();

            if (!this.showDimmerTimeoutId)
            {
                this.showDimmerTimeoutId = window.setTimeout(() =>
                {
                    this._updateDisplay();
                }, this.waitTimeInMilliseconds);
            }
        }
        else if (change.originalValue && !change.currentValue)
        {
            if (this.showDimmerTimeoutId)
            {
                clearTimeout(this.showDimmerTimeoutId);
                this.showDimmerTimeoutId = 0;
            }

            this._updateDisplay();
        }
    }
    protected _totalChanged(change: PropertyChange<number>)
    {
        if (this.mode.get() === "progress")
            this.progress?.total.set(change.currentValue);
    }
    protected _updateDisplay()
    {
        if (this.updatingDisplay)
            return;

        const
            queueLength = this.queueLength.get() ?? 0;

        if (queueLength > 0 && !this.dimmerShown)
        {
            this.updatingDisplay = true;

            this.dimmer.showElementOrControlAsync(this)
                .then(() =>
                {
                    this.dimmerShown = true;
                    this.updatingDisplay = false;
                    this._updateDisplay();
                });
        }
        else if (queueLength === 0 && this.dimmerShown)
        {
            this.updatingDisplay = true;

            this.dimmer.hideElementOrControlAsync(this)
                .then(() =>
                {
                    this.dimmerShown = false;
                    this.updatingDisplay = false;
                    this._updateDisplay();
                });
        }
    }
    protected _valueChanged(change: PropertyChange<number>)
    {
        if (this.mode.get() === "progress")
            this.progress?.value.set(change.currentValue);
    }

    // #endregion

    // #region Private Methods

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: FullPageLoaderPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.mode =
            new Property<FullPageLoaderModeEnum>(
                this,
                "System.String, System.Private.CoreLib",
                propertyValues.mode,
                this._modeChanged,
                undefined,
                [
                    new ClientAwareCategoryAttribute({ category: "Behavior" }),
                    new ClientAwareDisplayNameAttribute({ displayName: "Display Mode" }),
                    // TODO: BAM
                    //new PropertyEditorAttribute(PropertyEditorComboBox, {
                    //    listDataSource: [
                    //        { value: FullPageLoaderModeEnum.loader, description: "Loader" },
                    //        { value: FullPageLoaderModeEnum.progress, description: "Progress" }
                    //    ]
                    //}),
                ],
            );

        this.queueLength = new PropertyInt32(this, propertyValues.queueLength, this._queueLengthChanged);
        this.total = new PropertyInt32(this, propertyValues.total, this._totalChanged);
        this.value = new PropertyInt32(this, propertyValues.value, this._valueChanged);
    }

    protected override _createElement()
    {
        const
            $$element = super._createElement();

        switch (this.mode.get())
        {
            case "loader":
                {
                    $$(StringHelper.formatWithHtmlEncode("<div class=\"ui text loader\"><p>{0}</p></div>", this.text.get()))
                        .appendTo($$element);

                    break;
                }
            case "progress":
                {
                    this.progress = new ProgressBar({ total: this.total.get(), text: this.text.get(), invertStyles: true });
                    this.progress.appendTo($$element);

                    this.addCssClass("rad-mode-progress");

                    break;
                }
        }

        return $$element;
    }
    protected override _textChanged(change: PropertyChange<string>)
    {
        super._textChanged(change);

        switch (this.mode.get())
        {
            case "loader":
                {
                    this.$$element.get()?.find("> .loader > p").text(change.currentValue);
                    break;
                }
            case "progress":
                {
                    this.progress?.text.set(change.currentValue);
                    break;
                }
        }
    }

    // #endregion

    // #endregion
}